import { create } from 'zustand';
import { addDays, format, parse } from 'date-fns';
import {
  getReservationBookingAvailabilityApi,
  getReservationBookingStoresApi,
  getReservationCmsItemListApi,
  ReservationBookingAvailabilityParams,
} from 'app/services/reservationHistoryApi';
import {
  DestinationOption,
  getBookingMethodArr,
  ReservationCardConfig,
} from './config';
import { ReservationSearchCondition } from './config';
import type { ReservationBookingContentValue } from './ReservationBookingContent';
import { showReservationErrorToast } from '@/lib/utils';
import {
  formatReservationPriceRange,
  getCuisineTypes,
  getFirstMediaUrl,
  getReservationCmsPayload as sharedGetReservationCmsPayload,
  mergeVariantCmsItem as sharedMergeVariantCmsItem,
} from './reservationCmsUtils';
import { DEFAULT_RESTAURANT_IMAGE } from './constants';
import { fetchReservationProductWithAvailability } from './store_API_products_old';

export type ReservationPagination = {
  /** pageIndex dùng chuẩn zero-based để slice dữ liệu trên FE. */
  pageIndex: number;
  /** pageSize quyết định số nhà hàng hiển thị mỗi trang. */
  pageSize: number;
};

/**
 * Trả ngày hiện tại theo dd/MM/yyyy để form tìm kiếm và API cùng hiểu một ngày.
 * Nghiệp vụ cần một giá trị mặc định hợp lệ ngay khi khách mở trang đặt bàn;
 * nếu mỗi nơi tự định dạng, có thể tìm nhầm ngày hoặc không tìm thấy suất.
 */
export const getTodayReservationDate = () => format(new Date(), 'dd/MM/yyyy');

export const buildRestaurantDetailUrl = (
  restaurantId: string | number,
  bookingRules?: any,
) => {
  const rawMinGuestCount =
    typeof bookingRules === 'number'
      ? bookingRules
      : bookingRules?.minGuestCount ?? bookingRules?.min_guest_count;
  const minGuestCount = Number(rawMinGuestCount);
  const params = new URLSearchParams({
    bookingDate: getTodayReservationDate(),
    adult: `${Number.isFinite(minGuestCount) && minGuestCount > 0 ? minGuestCount : 1}`,
    child: '0',
  });

  return `/restaurants/${restaurantId}?${params.toString()}`;
};

export interface ReservationTableState {
  /** Danh sách card đã được lọc và phân trang để render lên UI. */
  data: ReservationCardConfig[];
  /** Payload sản phẩm đặt bàn lấy từ API; giữ lại để lọc/phân trang phía client mà không gọi API lại liên tục. */
  reservationProduct?: any;
  /** Dữ liệu đặt bàn tạm thời khi user chọn nhà hàng nhưng chưa hoàn tất flow booking. */
  bookingDraft?: {
    restaurant: ReservationCardConfig;
    booking: ReservationBookingContentValue;
  };
  /** Tổng số item sau khi lọc, dùng để render pagination chính xác. */
  totalCount: number;
  /** Danh sách điểm đến dùng cho bộ lọc destination. */
  destinationOptions: DestinationOption[];
  /** 7 ngày gần nhất/tới hiện tại cho field ngày đặt bàn. */
  dateOptions_NgayDatBan: string[];
  /** Trạng thái phân trang hiện tại của bảng/card list. */
  pagination: ReservationPagination;
  /** Điều kiện tìm kiếm/lọc hiện tại, được giữ trong store để pagination dùng lại. */
  searchCondition: ReservationSearchCondition;
  /** Cờ loading giúp UI hiển thị trạng thái đang lấy/chuẩn bị dữ liệu. */
  isLoadingDataTable: boolean;
  /**
   * Lưu hoặc xóa bản nháp đặt bàn đang làm dở, gồm nhà hàng đã chọn và thông tin
   * khách nhập. Các màn trong quy trình dùng chung bản nháp để khách không phải
   * chọn lại nhà hàng hay nhập lại thông tin khi chuyển bước.
   */
  setBookingDraft: (draft?: ReservationTableState['bookingDraft']) => void;
  /**
   * Tải lại danh sách lựa chọn đặt bàn và tình trạng chỗ theo điều kiện mới.
   * Dùng khi ngày hoặc số khách thay đổi vì suất còn chỗ và quy tắc nhận khách
   * có thể khác nhau theo từng ngày, khung giờ và cách đặt.
   */
  refreshReservationProduct: (
    searchCondition: ReservationSearchCondition,
    storeId?: string,
  ) => Promise<any>;
  /**
   * Xóa dữ liệu tìm kiếm, kết quả, phân trang và bản nháp khi khách rời luồng
   * nhà hàng. Việc reset tránh hiển thị nhầm dữ liệu của phiên đặt bàn trước
   * khi khách quay lại.
   */
  resetReservationState: () => void;
  /**
   * Cập nhật trang đang xem và dựng lại danh sách theo bộ lọc. Có thể buộc tải
   * mới dữ liệu khi điều kiện ảnh hưởng tới tồn chỗ thay đổi; đổi kích thước
   * trang sẽ đưa khách về trang đầu để không rơi vào trang không còn dữ liệu.
   */
  setPagination: (
    update:
      | ReservationPagination
      | ((prev: ReservationPagination) => ReservationPagination),
    formSearch?: ReservationSearchCondition,
    options?: { forceFetch?: boolean },
  ) => Promise<void>;
  /**
   * Đổi số trang UI (bắt đầu từ 1) sang chỉ số nội bộ (bắt đầu từ 0), rồi
   * áp dụng trang và kích thước trang mới cho danh sách nhà hàng.
   */
  handlePaginationChange: (page: number, pageSize: number) => void;
}

/** Tạo object search mặc định mới mỗi lần cần reset để tránh dùng chung reference nested object. */
export const createDefaultReservationSearch =
  (): ReservationSearchCondition => ({
    restaurants: [],
    bookingDate: getTodayReservationDate(),
    guests: {
      adult: 1,
      child: 0,
    },
    filters: {
      times: [],
      priceRanges: [],
      cuisines: [],
    },
    destinations: [],
  });

export const defaultReservationSearch = createDefaultReservationSearch();

/** Bóc phần dữ liệu availability khỏi các dạng bọc response mà API có thể trả. */
const getAvailabilityPayload = (response: any) =>
  response?.data?.data ?? response?.data ?? response;

/**
 * Chuẩn hóa danh sách nhà hàng từ API: giữ các variant để hiển thị/đặt bàn và
 * tạo danh sách site duy nhất làm lựa chọn điểm đến, tránh lặp địa điểm trong filter.
 */
const normalizeStoreResponse = (stores: any[]) => ({
  sites: Array.from(
    new Map(
      stores
        .filter(store => store?.siteId)
        .map(store => [
          store.siteId,
          { id: store.siteId, name: store.siteName },
        ]),
    ).values(),
  ),
  variants: stores,
});

/**
 * Lấy bộ quy tắc áp dụng đúng cách đặt của variant (đặt cọc hoặc đặt vé).
 * Một nhà hàng có thể nhận nhiều hình thức đặt với điều kiện số khách và thời
 * gian báo trước khác nhau, nên cần chọn đúng rule trước khi lọc suất.
 */
const getVariantBookingRule = (variant: any, bookingMethod?: string) => {
  const rules = variant?.bookingRules ?? variant?.booking_rules ?? {};
  if (Array.isArray(rules)) {
    return (
      rules.find((rule: any) => rule?.bookingMethod === bookingMethod) ?? {}
    );
  }
  if (rules?.bookingMethod && rules.bookingMethod !== bookingMethod) return {};
  return rules?.[bookingMethod ?? ''] ?? rules;
};

/** Quy đổi giới hạn đặt trước từ phút/giờ/ngày sang phút để so sánh thống nhất. */
const getAdvanceMinutes = (value: unknown, unit: unknown) => {
  const numericValue = Number(value ?? 0);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return 0;

  switch (String(unit ?? 'HOUR').toUpperCase()) {
    case 'MINUTE':
      return numericValue;
    case 'DAY':
      return numericValue * 24 * 60;
    default:
      return numericValue * 60;
  }
};

/**
 * Trả các giờ nhận đặt của variant trong ngày đã chọn, bỏ kỳ không hoạt động
 * hoặc không áp dụng vào đúng thứ trong tuần. Đây là các giờ có thể hỏi API
 * availability, không phải cam kết còn bàn.
 */
const getVariantReservationTimes = (variant: any, reservationDate: string) => {
  const periods = Array.isArray(variant?.periods) ? variant.periods : [];
  const dayOfWeek = parse(reservationDate, 'yyyy-MM-dd', new Date()).getDay();
  const times = periods.flatMap((period: any): string[] => {
    if (period?.isActive === false) return [];
    if (
      Array.isArray(period?.daysOfWeek) &&
      !period.daysOfWeek.includes(dayOfWeek)
    ) {
      return [];
    }

    const slots = Array.isArray(period?.timeSlots)
      ? period.timeSlots
      : Array.isArray(period?.time_slots)
      ? period.time_slots
      : [];
    return slots.map((slot: any) => String(slot?.value ?? '')).filter(Boolean);
  });

  return Array.from(new Set<string>(times));
};

/** Đổi chuỗi giờ hợp lệ thành số phút trong ngày để so sánh khung giờ. */
const getClockMinutes = (value?: string) => {
  const match = String(value ?? '').match(/(\d{1,2}):(\d{2})/);
  if (!match) return undefined;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return undefined;
  return hour * 60 + minute;
};

/**
 * Kiểm tra giờ khách muốn đặt có thuộc khung phục vụ API trả về không;
 * hỗ trợ cả khung bắt đầu/kết thúc lẫn dữ liệu chỉ có một giờ cụ thể.
 */
const isTimeInsidePeriod = (time: string, period: any) => {
  const timeMinutes = getClockMinutes(time);
  if (timeMinutes === undefined) return false;
  const startMinutes = getClockMinutes(period?.startTime ?? period?.start_time);
  const endMinutes = getClockMinutes(period?.endTime ?? period?.end_time);
  if (startMinutes !== undefined && endMinutes !== undefined) {
    return timeMinutes >= startMinutes && timeMinutes <= endMinutes;
  }
  const periodTime =
    period?.reservationTime ??
    period?.time ??
    period?.value ??
    period?.startTime;
  return periodTime
    ? getClockMinutes(String(periodTime)) === timeMinutes
    : false;
};

/**
 * Xác nhận một suất phù hợp điều kiện nhận đặt: số khách nằm trong giới hạn,
 * thời gian đặt không quá sát hoặc quá xa so với hiện tại. Các suất vi phạm
 * cần bị loại để khách không chọn giờ mà nhà hàng không nhận theo chính sách.
 */
const isTimeWithinBookingRule = (
  reservationDate: string,
  reservationTime: string,
  partySize: number,
  bookingRule: any,
) => {
  const minGuestCount = Number(
    bookingRule?.minGuestCount ?? bookingRule?.min_guest_count ?? 0,
  );
  const maxGuestCount = Number(
    bookingRule?.maxGuestCount ?? bookingRule?.max_guest_count ?? 0,
  );
  if (
    Number.isFinite(minGuestCount) &&
    minGuestCount > 0 &&
    partySize < minGuestCount
  ) {
    return false;
  }
  if (
    Number.isFinite(maxGuestCount) &&
    maxGuestCount > 0 &&
    partySize > maxGuestCount
  ) {
    return false;
  }

  const timeMatch = String(reservationTime).match(/(\d{1,2}):(\d{2})/);
  if (!timeMatch) return false;
  const slotDate = parse(reservationDate, 'yyyy-MM-dd', new Date());
  slotDate.setHours(Number(timeMatch[1]), Number(timeMatch[2]), 0, 0);
  if (Number.isNaN(slotDate.getTime())) return false;

  const minutesUntilSlot = (slotDate.getTime() - Date.now()) / 60000;
  const minAdvanceMinutes = getAdvanceMinutes(
    bookingRule?.minAdvanceValue ?? bookingRule?.min_advance_hours,
    bookingRule?.minAdvanceUnit ?? bookingRule?.min_advance_unit,
  );
  const maxAdvanceMinutes = getAdvanceMinutes(
    bookingRule?.maxAdvanceValue ?? bookingRule?.max_advance_value,
    bookingRule?.maxAdvanceUnit ?? bookingRule?.max_advance_unit,
  );

  if (minAdvanceMinutes > 0 && minutesUntilSlot < minAdvanceMinutes)
    return false;
  if (maxAdvanceMinutes > 0 && minutesUntilSlot > maxAdvanceMinutes)
    return false;
  return true;
};

/**
 * Tạo yêu cầu kiểm tra chỗ cho từng variant/giờ hợp lệ theo ngày và tổng số
 * người lớn + trẻ em. Lọc trước cả min/max guest và min/max advance để không
 * gọi BE cho lựa chọn nhà hàng vốn không nhận đặt theo rule.
 */
const buildAvailabilityParams = (
  product: any,
  searchCondition: ReservationSearchCondition,
): (Omit<
  ReservationBookingAvailabilityParams,
  'variantId' | 'zoneId' | 'reservationTime'
> & {
  storeId: string;
  zoneId?: string;
  reservationTime?: string;
  bookingMethod: 'DEPOSIT_RESERVATION' | 'TICKET_RESERVATION';
  eligibleReservationTimes: string[];
  bookingRule: any;
})[] => {
  const reservationDate = searchCondition.bookingDate
    ? format(
        parse(searchCondition.bookingDate, 'dd/MM/yyyy', new Date()),
        'yyyy-MM-dd',
      )
    : '';
  const partySize =
    Number(searchCondition.guests?.adult ?? 0) +
    Number(searchCondition.guests?.child ?? 0);

  if (!reservationDate || partySize <= 0) return [];

  return (product?.variants ?? []).flatMap((variant: any) => {
    const variantId = getVariantId(variant);
    if (!variantId) return [];

    const bookingMethod = getBookingMethodArr(variant?.bookingMethods);
    const bookingRule = getVariantBookingRule(variant, bookingMethod);
    const minGuestCount = Number(
      bookingRule?.minGuestCount ?? bookingRule?.min_guest_count ?? 0,
    );
    if (Number.isFinite(minGuestCount) && partySize < minGuestCount) return [];

    // Ticket reservations query availability once per store. The API returns
    // periods with their own bookable counts, so zones and individual times
    // are not part of this request.
    if (bookingMethod === 'TICKET_RESERVATION') {
      return [
        {
          storeId: String(variant.storeId ?? variantId),
          partySize,
          reservationDate,
          bookingMethod,
          eligibleReservationTimes: [],
          bookingRule,
        },
      ];
    }

    const reservationTimes = getVariantReservationTimes(
      variant,
      reservationDate,
    );

    if (!reservationTimes.length) return [];

    const zoneIds = (variant?.zones ?? [])
      .filter((zone: any) => zone?.isActive !== false)
      .map((zone: any) =>
        String(zone?.zoneId ?? zone?.zone_id ?? zone?.id ?? ''),
      )
      .filter(Boolean);
    const requestZoneIds: (string | undefined)[] = zoneIds.length
      ? zoneIds
      : [undefined];

    return reservationTimes
      .filter((reservationTime: string) =>
        isTimeWithinBookingRule(
          reservationDate,
          reservationTime,
          partySize,
          bookingRule,
        ),
      )
      .flatMap((reservationTime: string) =>
        requestZoneIds.map(zoneId => ({
          storeId: String(variant.storeId ?? variantId),
          zoneId,
          partySize,
          reservationDate,
          reservationTime: String(reservationTime),
          bookingMethod,
          eligibleReservationTimes: [String(reservationTime)],
          bookingRule,
        })),
      );
  });
};

/**
 * Gạn response availability theo giờ và booking rule đã chọn. Mục tiêu là chỉ
 * giữ suất khách thực sự được phép đặt; nếu rule thời gian/số khách không đạt,
 * đánh dấu hết chỗ để UI không mở tiếp luồng đặt cho suất đó.
 */
const filterAvailabilityByBookingPeriods = (
  availability: any,
  params: {
    reservationDate: string;
    partySize: number;
    reservationTime?: string;
    eligibleReservationTimes: string[];
    bookingRule: any;
  },
) => {
  if (
    !isTimeWithinBookingRule(
      params.reservationDate,
      params.reservationTime || '',
      params.partySize,
      params.bookingRule,
    )
  ) {
    return { ...availability, periods: [], bookable: 0, isAvailable: false };
  }

  const periods = Array.isArray(availability?.periods)
    ? availability.periods
    : undefined;
  if (!periods) return availability;

  const filteredPeriods = periods.flatMap((period: any) => {
    const slots = Array.isArray(period?.timeSlots)
      ? period.timeSlots
      : Array.isArray(period?.time_slots)
      ? period.time_slots
      : undefined;

    if (slots) {
      const filteredSlots = slots.filter((slot: any) => {
        const slotTime = String(
          slot?.value ?? slot?.time ?? slot?.startTime ?? '',
        );
        return (
          params.eligibleReservationTimes.includes(slotTime) &&
          isTimeWithinBookingRule(
            params.reservationDate,
            slotTime,
            params.partySize,
            params.bookingRule,
          )
        );
      });
      return filteredSlots.length
        ? [
            {
              ...period,
              [Array.isArray(period?.timeSlots) ? 'timeSlots' : 'time_slots']:
                filteredSlots,
            },
          ]
        : [];
    }

    const matchingTime = params.eligibleReservationTimes.find(
      time =>
        isTimeInsidePeriod(time, period) &&
        isTimeWithinBookingRule(
          params.reservationDate,
          time,
          params.partySize,
          params.bookingRule,
        ),
    );
    return matchingTime ? [period] : [];
  });

  const periodBookableValues = filteredPeriods
    .flatMap((period: any) => [
      period,
      ...(Array.isArray(period?.timeSlots)
        ? period.timeSlots
        : Array.isArray(period?.time_slots)
        ? period.time_slots
        : []),
    ])
    .map((entry: any) => {
      if (entry?.isAvailable === false) return 0;
      if (entry?.bookable !== undefined) return Number(entry.bookable);
      return entry?.isAvailable === true ? 1 : Number.NaN;
    })
    .filter((value: number) => Number.isFinite(value));
  const bookable = filteredPeriods.length
    ? periodBookableValues.length
      ? Math.max(...periodBookableValues)
      : availability?.bookable
    : 0;

  return {
    ...availability,
    periods: filteredPeriods,
    bookable,
    isAvailable:
      availability?.isAvailable !== false && Number(bookable ?? 0) > 0,
  };
};

/**
 * Gắn kết quả kiểm tra chỗ trở lại từng variant theo storeId, đồng thời giữ
 * danh sách availability tổng để các màn hiện tại có thể đọc theo cấu trúc cũ.
 */
const mergeAvailability = (product: any, results: any[]) => {
  const variants = (product?.variants ?? []).map((variant: any) => {
    const variantResults = results.filter(
      result =>
        String(result.data?.storeId ?? result.storeId) ===
        String(variant.storeId),
    );
    const availability = variantResults
      .map(result => result.data)
      .filter(Boolean);

    const mergedVariant = availability.length
      ? { ...variant, availability }
      : variant;
    const isTicketReservation =
      getBookingMethodArr(variant?.bookingMethods) === 'TICKET_RESERVATION';

    if (!isTicketReservation || !availability.length) return mergedVariant;

    const availabilityPeriods = availability.flatMap((item: any) =>
      Array.isArray(item?.periods) ? item.periods : [],
    );
    const timeSlotInformation = (
      Array.isArray(variant?.periods) ? variant.periods : []
    ).flatMap((listingPeriod: any) => {
      const timeSlots = Array.isArray(listingPeriod?.timeSlots)
        ? listingPeriod.timeSlots
        : Array.isArray(listingPeriod?.time_slots)
        ? listingPeriod.time_slots
        : [];

      return timeSlots.flatMap((timeSlot: any) => {
        const timeSlotValue = String(timeSlot?.value ?? '');
        const availabilityPeriod = availabilityPeriods.find((period: any) =>
          isTimeInsidePeriod(timeSlotValue, period),
        );

        return availabilityPeriod
          ? [
              {
                ...availabilityPeriod,
                timeSlotValue,
                timeSlotLabel: timeSlot?.label ?? timeSlotValue,
              },
            ]
          : [];
      });
    });

    return { ...mergedVariant, timeSlotInformation };
  });

  return {
    ...product,
    availability: results.map(result => result.data).filter(Boolean),
    variants,
  };
};

/**
 * Tải dữ liệu chính cho màn tìm nhà hàng: lấy danh sách địa điểm/variants,
 * bổ sung nội dung CMS, hỏi tình trạng chỗ cho các giờ nhận đặt và ghép tất cả
 * về một sản phẩm thống nhất. API availability chạy song song để giảm thời
 * gian chờ; lỗi của một khung giờ chỉ loại kết quả khung đó, không làm mất cả
 * danh sách nhà hàng.
 */
export const loadReservationProductFromStoreApis = async (
  searchCondition: ReservationSearchCondition,
  targetStoreId?: string,
) => {
  const guestQuantity =
    Number(searchCondition.guests?.adult ?? 0) +
    Number(searchCondition.guests?.child ?? 0);
  const response = await getReservationBookingStoresApi({
    bookingDate: searchCondition.bookingDate,
    adultQuantity: guestQuantity,
    childQuantity: 0,
  });
  const product = normalizeStoreResponse(response.data);
  const params = buildAvailabilityParams(product, searchCondition).filter(
    item => !targetStoreId || String(item.storeId) === String(targetStoreId),
  );
  const eligibleVariantIds = new Set(params.map(item => String(item.storeId)));
  const ticketParams = params.filter(
    item => item.bookingMethod === 'TICKET_RESERVATION',
  );
  const depositParams = params.filter(
    item => item.bookingMethod !== 'TICKET_RESERVATION',
  );
  const [cmsItems, results] = await Promise.all([
    fetchReservationCmsItemListByVariants(product.variants),
    (async () => {
      const ticketResults = ticketParams.length
        ? await getReservationBookingAvailabilityApi(
            ticketParams.map(
              ({ storeId, partySize, reservationDate, bookingMethod }) => ({
                storeId,
                partySize: partySize!,
                reservationDate: reservationDate!,
                bookingMethod: bookingMethod!,
                isFree: 1
              }),
            ),
          )
            .then(response => {
              const payload =
                response?.data?.data ?? response?.data ?? response;
              const entries = Array.isArray(payload) ? payload : [payload];
              return entries.flatMap((entry: any, index: number) => {
                const isBatchEntry =
                  entry &&
                  (Object.prototype.hasOwnProperty.call(entry, 'isSuccess') ||
                    Object.prototype.hasOwnProperty.call(entry, 'itemIndex'));
                if (isBatchEntry && !entry?.data) {
                  return [];
                }
                const availability = isBatchEntry ? entry.data : entry;
                if (!availability) return [];
                const responseStoreId = String(availability?.storeId ?? '');
                const itemIndex =
                  entry?.itemIndex === null || entry?.itemIndex === undefined
                    ? undefined
                    : Number(entry.itemIndex);
                const request =
                  ticketParams.find(
                    item => String(item.storeId) === responseStoreId,
                  ) ??
                  ticketParams[
                    itemIndex !== undefined &&
                    Number.isInteger(itemIndex) &&
                    itemIndex >= 0
                      ? itemIndex
                      : index
                  ];
                if (!request) return [];
                return [
                  {
                    ...request,
                    data: {
                      ...availability,
                      apiErrors: Array.isArray(entry?.errors)
                        ? entry.errors
                        : [],
                      bookingMethod: request?.bookingMethod,
                    },
                  },
                ];
              });
            })
            .catch((error: any) => {
              return [];
            })
        : [];
      const depositResults = await Promise.all(
        depositParams.map(async paramsItem => {
          try {
            const availabilityResponse =
              await getReservationBookingAvailabilityApi({
                storeId: paramsItem.storeId,
                partySize: paramsItem.partySize!,
                reservationDate: paramsItem.reservationDate!,
                bookingMethod: paramsItem.bookingMethod!,
              });
            const availability = filterAvailabilityByBookingPeriods(
              getAvailabilityPayload(availabilityResponse),
              paramsItem,
            );
            return {
              ...paramsItem,
              data: {
                ...availability,
                zoneId: paramsItem.zoneId,
                zoneID:
                  availability?.zoneID ??
                  availability?.zoneId ??
                  paramsItem.zoneId,
                reservationTime: paramsItem.reservationTime,
                bookingMethod: paramsItem.bookingMethod,
              },
            };
          } catch {
            return null;
          }
        }),
      );
      return [...ticketResults, ...depositResults];
    })(),
  ]);

  const productWithAvailability = mergeAvailability(
    mergeReservationCmsItems(product, cmsItems),
    results.filter(Boolean) as any[],
  );

  return {
    ...productWithAvailability,
    variants: (productWithAvailability.variants ?? []).map((variant: any) => ({
      ...variant,
      isBookingRuleEligible: eligibleVariantIds.has(
        String(variant.storeId ?? getVariantId(variant)),
      ),
    })),
  };
};

/**
 * Chuyển site thành lựa chọn lọc điểm đến và thêm lựa chọn “Tất cả”. Khách có
 * thể xem mọi địa điểm hoặc thu hẹp danh sách theo một site cụ thể.
 */
const buildDestinationOptionsFromSites = (
  sites: any[],
): DestinationOption[] => {
  if (!sites.length) return [];

  const mappedSites = sites
    .map(site => ({
      id: site?.id,
      labelKey: site?.name,
      fallback: site?.name,
    }))
    .filter(item => item.id && item.fallback);

  if (!mappedSites.length) return [];

  return [
    {
      id: 'all',
      labelKey: 'restaurant.destination.all',
      fallback: 'Tất cả',
    },
    ...mappedSites,
  ];
};

/**
 * Chọn ảnh đại diện đầu tiên có dữ liệu từ các tên trường API/CMS khác nhau;
 * nếu không có ảnh thì dùng ảnh mặc định để card nhà hàng vẫn hiển thị ổn định.
 */
export const getImageUrl = (item: any) => {
  const media = item?.media;
  const images = item?.images;

  return (
    [
      item?.image,
      item?.imageUrl,
      item?.thumbnail,
      item?.thumbnailUrl,
      item?.avatar,
      item?.banner,
      item?.store?.image,
      item?.store?.imageUrl,
      Array.isArray(media) ? media[0]?.url ?? media[0] : media?.url,
      Array.isArray(images) ? images[0]?.url ?? images[0] : images?.url,
    ].find(Boolean) ?? DEFAULT_RESTAURANT_IMAGE
  );
};

/**
 * Gán nhà hàng vào một nhóm giá chuẩn dựa trên giá tham chiếu. Filter giá dùng
 * id nhóm thống nhất để hoạt động dù dữ liệu đầu vào chỉ có giá thấp/cao.
 */
const getReservationPriceRangeId = (item: any) => {
  const priceFrom = Number(item?.priceRangeFrom);
  const priceTo = Number(item?.priceRangeTo);
  const referencePrice = Number.isFinite(priceFrom)
    ? priceFrom
    : Number.isFinite(priceTo)
    ? priceTo
    : undefined;

  if (referencePrice === undefined) return item?.priceRangeId ?? '';
  if (referencePrice < 100000) return '1';
  if (referencePrice < 300000) return '2';
  if (referencePrice < 500000) return '3';

  return '4';
};

/**
 * Quy đổi cách gọi khung giờ khác nhau từ API/CMS về id cố định (sáng, trưa/
 * chiều, tối), giúp bộ lọc giờ không phụ thuộc ngôn ngữ hoặc cách đặt tên.
 */
const normalizePeriodId = (period: any) => {
  const value = `${
    period?.period_name ?? period?.periodName ?? ''
  }`.toLowerCase();

  if (['1', 'morning', 'sang', 'sáng'].some(v => value.includes(v))) return '1';
  if (
    ['2', 'afternoon', 'noon', 'lunch', 'trua', 'trưa', 'chieu', 'chiều'].some(
      v => value.includes(v),
    )
  )
    return '2';
  if (['3', 'evening', 'night', 'toi', 'tối'].some(v => value.includes(v)))
    return '3';
  return value || undefined;
};

/** Lấy các nhóm giờ phục vụ của nhà hàng để so khớp với bộ lọc giờ khách chọn. */
export const getVariantTimeIds = (variant: any) => {
  if (Array.isArray(variant?.periods)) {
    return variant.periods.map(normalizePeriodId).filter(Boolean);
  }

  return [];
};

/** Lấy mã định danh variant theo thứ tự tên trường các phiên bản API hỗ trợ. */
export const getVariantId = (variant: any) =>
  variant?.variantId ?? variant?.id ?? variant?.storeId;

/** Lấy số khách tối thiểu của cách đặt được áp dụng cho variant. */
const getVariantMinGuestCount = (variant: any) => {
  const bookingMethod = getBookingMethodArr(variant?.bookingMethods);
  const rules = getVariantBookingRule(variant, bookingMethod);
  const minGuestCount = Number(
    rules?.minGuestCount ?? rules?.min_guest_count ?? 0,
  );

  return Number.isFinite(minGuestCount) ? minGuestCount : 0;
};

/** Ẩn nhà hàng/loại đặt nếu tổng số khách thấp hơn mức tối thiểu nhà hàng nhận. */
const isVariantSatisfyGuestCount = (
  variant: any,
  searchCondition: ReservationSearchCondition,
) => {
  const partySize =
    Number(searchCondition.guests?.adult ?? 0) +
    Number(searchCondition.guests?.child ?? 0);
  const minGuestCount = getVariantMinGuestCount(variant);

  return !minGuestCount || partySize >= minGuestCount;
};

/** Một khu vực/suất còn đặt được khi API báo số chỗ khả dụng lớn hơn 0. */
const getAvailabilityErrors = (availabilityData: any) => {
  const periods = Array.isArray(availabilityData?.periods)
    ? availabilityData.periods
    : [];
  const nestedSlots = periods.flatMap((period: any) => [
    ...(Array.isArray(period?.timeSlots) ? period.timeSlots : []),
    ...(Array.isArray(period?.time_slots) ? period.time_slots : []),
  ]);
  return [
    ...(Array.isArray(availabilityData?.apiErrors)
      ? availabilityData.apiErrors
      : []),
    ...(Array.isArray(availabilityData?.errors)
      ? availabilityData.errors
      : []),
    ...periods.flatMap((period: any) =>
      Array.isArray(period?.errors) ? period.errors : [],
    ),
    ...nestedSlots.flatMap((slot: any) =>
      Array.isArray(slot?.errors) ? slot.errors : [],
    ),
  ];
};

const isZoneBookable = (availabilityData: any) =>
  !getAvailabilityErrors(availabilityData).length &&
  Number(availabilityData?.bookable ?? 0) > 0;

/** Variant còn đặt được nếu ít nhất một khu vực hoặc suất của nó còn chỗ. */
const isVariantBookable = (variant: any) =>
  Array.isArray(variant?.availability) &&
  variant.availability.some(isZoneBookable);

/**
 * Lấy nội dung CMS cho các variant để bổ sung thông tin hiển thị như mô tả,
 * hình ảnh, ẩm thực. Một item lỗi không chặn các item còn lại để trang vẫn có
 * thể hiện danh sách nhà hàng từ API chính.
 */
export const fetchReservationCmsItemListByVariants = async (
  variants: any[] = [],
) => {
  const variantParams = variants
    .map(variant => ({
      variantId: getVariantId(variant),
      id: variant.storeId,
    }))
    .filter(item => item.id);

  if (!variantParams.length) {
    console.log('[CMS reservation item list] missing store_id in variants');
    return [];
  }

  const responses = await Promise.allSettled(
    variantParams.map(item =>
      getReservationCmsItemListApi(
        { id: item.id },
        { skipErrorNotify: true },
      ).then(response => ({
        ...item,
        data: response?.data ?? response,
      })),
    ),
  );

  return responses
    .filter(response => response.status === 'fulfilled')
    .map(response => {
      const value = (response as PromiseFulfilledResult<any>).value;

      return {
        ...value,
        data: sharedGetReservationCmsPayload(value.data),
      };
    });
};

/**
 * Ghép nội dung CMS vào variant tương ứng bằng storeId, giữ dữ liệu listing
 * làm nền và chỉ bổ sung/chuẩn hóa phần CMS phục vụ nội dung card.
 */
export const mergeReservationCmsItems = (
  reservationProduct: any,
  cmsItems: any[] = [],
) => {
  if (!cmsItems.length) return reservationProduct;

  const cmsItemByStoreId = cmsItems.reduce((acc, item) => {
    acc[`${item.id}`] = item.data;
    return acc;
  }, {} as Record<string, any>);

  const variants = (reservationProduct?.variants || []).map((variant: any) => {
    const cmsItem = cmsItemByStoreId[`${variant?.storeId ?? ''}`]?.data;
    if (!cmsItem) return variant;

    return sharedMergeVariantCmsItem(variant, cmsItem);
  });

  return {
    ...reservationProduct,
    variants,
  };
};

/**
 * Áp dụng các điều kiện khách chọn (nhà hàng, điểm đến, giờ, giá, ẩm thực) lên
 * card đã tải. Lọc phía client giúp đổi filter/phân trang nhanh mà không gọi
 * lại các API danh sách và tình trạng chỗ cho mỗi thao tác.
 */
const filterReservationCards = (
  cards: ReservationCardConfig[],
  searchCondition: ReservationSearchCondition,
) => {
  /** Chọn "all" nghĩa là không giới hạn destination. */
  const selectedDestinations = searchCondition.destinations.includes('all')
    ? []
    : searchCondition.destinations;

  return cards.filter(item => {
    const matchRestaurant =
      !searchCondition.restaurants.length ||
      searchCondition.restaurants.includes(item.id);
    const matchDestination =
      !selectedDestinations.length ||
      selectedDestinations.includes(item.destinationId);
    const matchTime =
      !searchCondition.filters.times.length ||
      searchCondition.filters.times.some(time => item.timeIds.includes(time));
    const matchPrice =
      !searchCondition.filters.priceRanges.length ||
      searchCondition.filters.priceRanges.includes(item.priceRangeId);
    const matchCuisine =
      !searchCondition.filters.cuisines.length ||
      searchCondition.filters.cuisines.some(cuisine =>
        item.cuisineIds.includes(cuisine),
      );

    return (
      matchRestaurant &&
      matchDestination &&
      matchTime &&
      matchPrice &&
      matchCuisine
    );
  });
};

/**
 * Chuyển dữ liệu API thành card nhà hàng mà UI hiểu, loại các lựa chọn không
 * nhận đủ số khách, áp dụng bộ lọc rồi cắt đúng trang. Hàm cũng gắn cờ hết chỗ
 * để UI phản ánh khả năng đặt của từng variant.
 */
const buildReservationTableData = (
  reservationProduct: any,
  searchCondition: ReservationSearchCondition,
  pagination: ReservationPagination,
) => {
  const cards = (reservationProduct.variants || [])
    .filter(
      (variant: any) =>
        variant?.isBookingRuleEligible !== false &&
        isVariantSatisfyGuestCount(variant, searchCondition),
    )
    .map(variant => {
      const cuisineTypes = getCuisineTypes(variant);

      return {
        id: getVariantId(variant),
        destinationId: variant?.siteId,
        timeIds: getVariantTimeIds(variant),
        priceRangeId: getReservationPriceRangeId(variant),
        cuisineIds: variant?.cuisineIds ?? cuisineTypes,
        titleKey: variant?.storeName,
        priceRangeKey: formatReservationPriceRange(variant),
        locationKey: variant?.address,
        image: getFirstMediaUrl(variant) ?? getImageUrl(variant),
        tagKeys: cuisineTypes,
        statusKey: '',
        availability: variant?.availability ?? [],
        isFullyBooked: !isVariantBookable(variant),
        bookingMethods: variant?.bookingMethods ?? [],
        booking_rules: variant?.bookingRules,
      };
    });
  const filteredCards = filterReservationCards(cards, searchCondition);
  const startIndex = pagination.pageIndex * pagination.pageSize;

  return {
    data: filteredCards.slice(startIndex, startIndex + pagination.pageSize),
    totalCount: filteredCards.length,
  };
};

/**
 * Store dùng chung cho luồng tìm và đặt nhà hàng: giữ kết quả, điều kiện lọc,
 * phân trang, trạng thái tải và bản nháp booking. Nhờ vậy các thành phần màn
 * hình nhất quán về lựa chọn của khách trong suốt một lượt đặt bàn.
 */
export const useReservationStore = create<ReservationTableState>()(
  (set, get) => ({
    data: [],
    reservationProduct: undefined,
    bookingDraft: undefined,
    totalCount: 0,
    destinationOptions: [],
    dateOptions_NgayDatBan: Array.from({ length: 7 }, (_, index) =>
      format(addDays(new Date(), index), 'dd/MM/yyyy'),
    ),
    pagination: { pageIndex: 0, pageSize: 6 },
    searchCondition: defaultReservationSearch,
    isLoadingDataTable: true,
    /** Giữ bản nháp giữa các bước đặt bàn hoặc xóa khi luồng đã kết thúc. */
    setBookingDraft: draft => set({ bookingDraft: draft }),
    /** Tải mới danh sách nhà hàng và tình trạng chỗ theo điều kiện tìm kiếm đã nhận. */
    refreshReservationProduct: async (searchCondition, storeId) => {
      const reservationProduct = await loadReservationProductFromStoreApis(
        searchCondition,
        storeId,
      );

      if (!storeId) set({ reservationProduct });

      return reservationProduct;
    },
    /** Dọn toàn bộ dữ liệu phiên tìm/đặt để lượt truy cập tiếp theo bắt đầu sạch. */
    resetReservationState: () =>
      set({
        data: [],
        reservationProduct: undefined,
        bookingDraft: undefined,
        totalCount: 0,
        destinationOptions: [],
        pagination: { pageIndex: 0, pageSize: 6 },
        searchCondition: createDefaultReservationSearch(),
        isLoadingDataTable: true,
      }),
    /** Nhận trang từ UI, đổi về zero-based và dùng lại điều kiện tìm kiếm hiện hành. */
    handlePaginationChange: (page, pageSize) => {
      get().setPagination(
        {
          pageIndex: page - 1,
          pageSize,
        },
        get().searchCondition,
      );
    },
    /**
     * Cập nhật danh sách sau thay đổi tìm kiếm hoặc phân trang. Tái sử dụng
     * sản phẩm đã tải khi chỉ đổi trang/bộ lọc; tải lại khi chưa có dữ liệu hoặc
     * được yêu cầu forceFetch. Trong lúc chờ bật loading; nếu API lỗi thì báo
     * lỗi và xóa kết quả cũ để khách không nhầm dữ liệu cũ là kết quả mới.
     */
    setPagination: async (update, formSearch, options) => {
      const current = get().pagination;
      const next = typeof update === 'function' ? update(current) : update;
      const safePagination = {
        pageIndex: next.pageSize !== current.pageSize ? 0 : next.pageIndex,
        pageSize: next.pageSize,
      };
      const searchCondition = formSearch ?? get().searchCondition;

      try {
        set(state => ({
          isLoadingDataTable: true,
          searchCondition: formSearch ?? state.searchCondition,
        }));

        let reservationProduct = options?.forceFetch
          ? undefined
          : get().reservationProduct;

        // Nếu chưa có dữ liệu hoặc cần làm mới tồn chỗ thì gọi API; còn lại dùng
        // dữ liệu đã tải để đổi trang/bộ lọc nhanh và tránh gọi API lặp lại.
        if (!reservationProduct) {
          reservationProduct =
            // await fetchReservationProductWithAvailability(searchCondition)
            await loadReservationProductFromStoreApis(searchCondition);
        }

        const tableData = buildReservationTableData(
          reservationProduct,
          searchCondition,
          safePagination,
        );

        set({
          data: tableData.data,
          reservationProduct,
          destinationOptions:
            buildDestinationOptionsFromSites(reservationProduct.sites) || [],
          totalCount: tableData.totalCount,
          pagination: safePagination,
          searchCondition,
          isLoadingDataTable: false,
        });
      } catch (error) {
        showReservationErrorToast(error);

        set({
          data: [],
          totalCount: 0,
          pagination: safePagination,
          searchCondition,
          isLoadingDataTable: false,
        });
      }
    },
  }),
);
